import {
  ClipboardDocumentCheckIcon,
  CurrencyDollarIcon,
  DocumentIcon,
  IdentificationIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useState } from 'react';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFormProvider } from '@/components/UI/HookForm/Provider';
import { CashAssets } from './CashAssets';
import { initialValues } from './constants';
import { EmploymentTax } from './EmploymentTax';
import { HomeownerInformation } from './HomeownerInformation';
import LandingPage from './LandingPage';
import { OtherIncome } from './OtherIncome';
import { Review } from './Review';
import type { HouseholdIncomeFormData, StepConfig } from './types';
import { householdIncomeValidationSchema } from './validationSchema';

interface HouseholdIncomeFormProps {
  formData?: {
    data?: {
      name?: string;
      confirmationMessage?: string;
    };
  };
}

const HouseholdIncomeForm = ({ formData }: HouseholdIncomeFormProps) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);

  const steps: StepConfig[] = [
    {
      name: 'Homeowner Information',
      sectionId: 'homeownerInformation',
      Icon: IdentificationIcon,
      component: <HomeownerInformation onNext={() => setCurrentStep((prev) => prev + 1)} />,
    },
    {
      name: 'Employment & Tax',
      sectionId: 'employmentTax',
      Icon: DocumentIcon,
      component: <EmploymentTax onNext={() => setCurrentStep((prev) => prev + 1)} onPrevious={() => setCurrentStep((prev) => prev - 1)} />,
    },
    {
      name: 'Cash Assets & Statements',
      sectionId: 'cashAssets',
      Icon: CurrencyDollarIcon,
      component: <CashAssets onNext={() => setCurrentStep((prev) => prev + 1)} onPrevious={() => setCurrentStep((prev) => prev - 1)} />,
    },
    {
      name: 'Other Income',
      sectionId: 'otherIncome',
      Icon: UserGroupIcon,
      component: <OtherIncome onNext={() => setCurrentStep((prev) => prev + 1)} onPrevious={() => setCurrentStep((prev) => prev - 1)} />,
    },
    {
      name: 'Review',
      sectionId: 'review',
      Icon: ClipboardDocumentCheckIcon,
      component: <Review onPrevious={() => setCurrentStep((prev) => prev - 1)} />,
    },
  ];

  const isLastStep = currentStep === steps.length - 1;

  const handleFormSubmit = useCallback(
    (values: HouseholdIncomeFormData) => {
      if (!isLastStep) {
        // Move to next step
        setCurrentStep((prev) => prev + 1);
      } else {
        // Final submission - since this is view-only, just show success
        console.log('Form submitted with values:', values);
        setIsSubmitSuccess(true);
      }
    },
    [isLastStep]
  );

  const handleStepNavigation = useCallback(
    (stepIndex: number) => {
      // Only allow navigation to current step or previous steps
      if (stepIndex <= currentStep) {
        setCurrentStep(stepIndex);
      }
    },
    [currentStep]
  );

  // Show success message after submission
  if (isSubmitSuccess) {
    return (
      <div className='min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8'>
        <div className='sm:mx-auto sm:w-full sm:max-w-md'>
          <div className='bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10'>
            <div className='text-center'>
              <ClipboardDocumentCheckIcon className='mx-auto h-12 w-12 text-primaryColor-normal' />
              <h2 className='mt-4 text-2xl font-extrabold text-gray-900'>Verification Complete</h2>
              <p className='mt-2 text-sm text-gray-600'>
                {formData?.data?.confirmationMessage ||
                  'Thank you for completing your household income verification checklist. Your information has been received and is being reviewed.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show landing page if terms haven't been accepted
  if (!hasAcceptedTerms) {
    return <LandingPage onAccept={() => setHasAcceptedTerms(true)} formName={formData?.data?.name || 'Household Income Verification'} />;
  }

  return (
    <div className='container mx-auto'>
      <h2 className='text-2xl font-bold text-gray-900 mb-6'>{formData?.data?.name || 'Household Income Verification Checklist'}</h2>

      <div className='flex mb-10'>
        {/* Sidebar Navigation */}
        <aside className='w-1/4 py-6 px-2 sm:px-6 bg-white rounded-l-md'>
          <nav className='space-y-1 py-5'>
            {steps.map(({ name, Icon, sectionId }, index) => (
              <button
                key={sectionId}
                type='button'
                className={`${
                  currentStep === index
                    ? 'bg-neutralColor-lightest text-primaryColor-normal'
                    : 'text-neutralColor-normal hover:text-primaryColor-normal hover:bg-neutralColor-lightest'
                } group rounded-md px-3 py-2 flex items-center text-sm font-medium w-full transition-colors text-start`}
                onClick={() => handleStepNavigation(index)}
                disabled={index > currentStep}
              >
                <Icon className={`shrink-0 -ml-1 mr-3 h-6 w-6 ${index > currentStep ? 'opacity-50' : ''}`} />
                <span className={index > currentStep ? 'opacity-50' : ''}>{name}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Form Content */}
        <div className='w-full bg-gray-50'>
          <HookFormProvider
            defaultValues={initialValues}
            mode='onChange'
            resolver={yupResolver(householdIncomeValidationSchema[currentStep] as any)}
          >
            <HookForm onSubmit={handleFormSubmit}>
              <div className='min-h-[600px]'>{steps[currentStep].component}</div>
            </HookForm>
          </HookFormProvider>
        </div>
      </div>
    </div>
  );
};

export default HouseholdIncomeForm;
